/**
 * Conformance — `selfCheck` for @norkron/web-sync-queue.
 *
 * Detects offline-queue visibility regressions: when `navigator.onLine === false`
 * AND the consumer has at least one pending operation, the consumer MUST surface
 * the queue depth via a visible-DOM indicator. We accept either:
 *
 *   - `[data-sync-status="offline-queue"]` (canonical marker), or
 *   - `[data-sync-status="offline"]` paired with a visible depth element
 *     (`[data-sync-queue-depth]`), or
 *   - any element carrying `[data-sync-queue-depth]` whose textContent is
 *     non-empty.
 *
 * The pending count is read from any element advertising
 * `[data-sync-queue-depth="N"]` OR `[data-norkron-sync-queue-pending="N"]`
 * (consumer-owned marker). If no marker is present in the DOM AND
 * `navigator.onLine === false`, we emit no finding (selfCheck cannot prove
 * a queue exists; that's a contract the consumer opts into).
 *
 * Registered codes (see `CODES.md`):
 *   - WEB_SYNC_QUEUE_DEPTH_HIDDEN_OFFLINE (major)
 *   - WEB_SYNC_QUEUE_SELFCHECK_BUDGET_EXCEEDED (info)
 *   - WEB_SYNC_QUEUE_SELFCHECK_INTERNAL_ERROR (info)
 *
 * Contract (per FR §4): pure, bounded, idempotent, no throws.
 */

export type ConformanceSeverity = 'critical' | 'major' | 'minor' | 'info'

export interface SelfCheckFinding {
	libId: string
	code: string
	category:
		| 'theme'
		| 'stylesheet'
		| 'a11y'
		| 'layout'
		| 'i18n'
		| 'cardinality'
		| 'identity'
		| 'schema'
		| 'flow'
		| 'deprecation'
		| 'perf'
	severity: ConformanceSeverity
	message: string
	selector?: string
	suggestedFix?: string
	extras?: Record<string, unknown>
}

export interface SelfCheckOptions {
	budgetMs?: number
	minSeverity?: ConformanceSeverity
	locale?: string
}

const LIB_ID = '@norkron/web-sync-queue'
const DEFAULT_BUDGET_MS = 50

const SEVERITY_RANK: Record<ConformanceSeverity, number> = {
	info: 0,
	minor: 1,
	major: 2,
	critical: 3,
}

interface ParsedOpts {
	budgetMs: number
	minSeverity: ConformanceSeverity
}

function parseOpts(opts?: SelfCheckOptions): ParsedOpts {
	const budget = opts?.budgetMs
	const sev = opts?.minSeverity
	const validSev: ConformanceSeverity[] = ['critical', 'major', 'minor', 'info']
	return {
		budgetMs: typeof budget === 'number' && Number.isFinite(budget) && budget > 0 ? budget : DEFAULT_BUDGET_MS,
		minSeverity: sev !== undefined && validSev.includes(sev) ? sev : 'info',
	}
}

function meetsFloor(finding: SelfCheckFinding, floor: ConformanceSeverity): boolean {
	return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor]
}

function now(): number {
	return typeof performance !== 'undefined' && typeof performance.now === 'function'
		? performance.now()
		: Date.now()
}

function isOnline(): boolean {
	if (typeof navigator === 'undefined') return true
	// undefined → assume online (no signal to act on).
	return navigator.onLine !== false
}

function readPendingDepth(root: Element): number {
	let total = 0
	const els = root.querySelectorAll<HTMLElement>(
		'[data-sync-queue-depth], [data-norkron-sync-queue-pending]',
	)
	for (const el of els) {
		const raw =
			el.getAttribute('data-sync-queue-depth') ??
			el.getAttribute('data-norkron-sync-queue-pending')
		if (raw === null) continue
		const n = Number.parseInt(raw, 10)
		if (Number.isFinite(n) && n > 0) total += n
	}
	return total
}

function hasVisibleOfflineIndicator(root: Element): boolean {
	// Canonical offline-queue marker.
	const offlineQueue = root.querySelector('[data-sync-status="offline-queue"]')
	if (offlineQueue !== null) return true
	// Generic offline marker + a depth element with non-empty text.
	const offline = root.querySelector('[data-sync-status="offline"]')
	if (offline !== null) {
		const depthEl = root.querySelector<HTMLElement>('[data-sync-queue-depth]')
		if (depthEl !== null && (depthEl.textContent ?? '').trim() !== '') return true
	}
	// Depth element with non-empty text content alone is acceptable.
	const depthOnly = root.querySelector<HTMLElement>('[data-sync-queue-depth]')
	if (depthOnly !== null && (depthOnly.textContent ?? '').trim() !== '') return true
	return false
}

/**
 * Run the sync-queue self-check on `root`.
 *
 * @example
 * ```ts
 * import { selfCheck } from '@norkron/web-sync-queue'
 * const findings = selfCheck(document.body)
 * ```
 */
export function selfCheck(root: Element, opts?: SelfCheckOptions): SelfCheckFinding[] {
	const start = now()
	const safeOpts = parseOpts(opts)
	const findings: SelfCheckFinding[] = []

	try {
		if (isOnline()) {
			return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity))
		}

		const depth = readPendingDepth(root)
		if (depth <= 0) {
			return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity))
		}

		if (now() - start > safeOpts.budgetMs) {
			findings.push({
				libId: LIB_ID,
				code: 'WEB_SYNC_QUEUE_SELFCHECK_BUDGET_EXCEEDED',
				category: 'perf',
				severity: 'info',
				message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms.`,
				extras: { budgetMs: safeOpts.budgetMs, elapsed: now() - start },
			})
			return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity))
		}

		if (!hasVisibleOfflineIndicator(root)) {
			findings.push({
				libId: LIB_ID,
				code: 'WEB_SYNC_QUEUE_DEPTH_HIDDEN_OFFLINE',
				category: 'a11y',
				severity: 'major',
				message: `navigator.onLine === false and the outbox has ${depth} pending item(s), but no visible-DOM indicator was found. Users have no way to see that writes are queued.`,
				suggestedFix:
					'Render a status element carrying [data-sync-status="offline-queue"], or a [data-sync-queue-depth] element with non-empty text content (e.g. "${depth} pending").',
				extras: { pendingDepth: depth },
			})
		}
	} catch (err) {
		findings.push({
			libId: LIB_ID,
			code: 'WEB_SYNC_QUEUE_SELFCHECK_INTERNAL_ERROR',
			category: 'identity',
			severity: 'info',
			message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`,
		})
	}

	return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity))
}
